import { onGeSdkEvent } from "@/libs/global-e/sdk-events";
import { openLocaleSwitcher } from "@/store/locale-store";

/**
 * GE 웰컴 팝업의 「CHANGE YOUR SHIPPING COUNTRY」 → **우리 국가 스위처**를 연다 (#5843).
 *
 * ## 무엇이 있었나 — 눌러도 아무 일도 안 일어났다
 *
 * dev(INT, AU) 실측 2026-09-29: 링크를 누르면 GE 가 `OnBeforeSwitcher` → `OnChangeShippingCountryClicked`
 * 를 **`glegem` 큐로** 발생시킨다. `OnBeforeSwitcher` 는 GE 가 **자기 스위처**를 열려는 신호인데 우리는
 * GE 스위처 UI 를 쓰지 않으므로(`docs/global-e-storefront.md` §3.5) 아무것도 안 뜬다. 그리고 **웰컴
 * 팝업과 회색 오버레이가 그대로 남는다.** GE(Kyuri) 요청: 이 이벤트에 우리 스위처를 띄울 것.
 *
 * ## ★GE 웰컴 팝업을 닫는다 — 안 닫으면 우리 다이얼로그가 뒤에 깔린다
 *
 * GE 팝업의 z-index 가 **1,000,000,000**(`#globale_popup`, 오버레이 `#globale_overlay` 는 1,000,001)이다
 * (실측). 우리 다이얼로그는 그 한참 아래라 팝업을 둔 채 열면 보이지 않는다.
 *
 * 닫는 수단은 GE 팝업의 ✕ 버튼이 부르는 `GlobalE.WelcomeManager.CloseWelcome(event)` 다. 인자 없이
 * 불러도 팝업·오버레이가 DOM 에서 사라지고, 공식 이벤트 `OnWelcomePopupClosed` 가 발생하며,
 * `GlobalE_Welcome_Data` 쿠키가 남아 다시 뜨지 않는다(실측 — ✕ 와 같은 결과).
 *
 * ⚠️ **`CloseWelcome` 은 GE 문서에 없는 내부 함수다.** 그래서 없거나 던져도 **스위처는 반드시 연다** —
 * 팝업이 남는 쪽이 아무것도 안 뜨는 지금보다 낫고, 사용자는 ✕ 로 닫을 수 있다. 실패는 경고로 남긴다
 * (GE 가 이 함수를 바꾸면 여기서 드러나게).
 */

type GeWindow = Window & {
  GlobalE?: {
    WelcomeManager?: { CloseWelcome?: (event?: unknown) => void };
    ShippingSwitcher?: { Close?: () => void };
  };
};

/**
 * ⚠️ **GE 메서드는 객체에 붙여서 부른다** — `manager.CloseWelcome()`. 함수만 떼어 부르면(`const close = …;
 * close()`) GE 안에서 `this` 가 `undefined` 가 되어 `Cannot read properties of undefined (reading
 * IsUndefined)` 로 던진다. #5849 가 정확히 그렇게 배포됐다 — dev 실제 코드 0/8: 두 GE 창이 안 닫히고 우리
 * 스위처만 그 아래에 열렸다(2026-09-29). 사전 dev 확인은 **주입한 코드**가 `window.GlobalE.WelcomeManager
 * .CloseWelcome()` 처럼 붙여 불러서 통과했고, 단위 테스트의 가짜는 `this` 를 안 써서 못 잡았다.
 * 가짜는 이제 `this` 를 쓴다(`welcome-change-country-this.test.ts`).
 */
export function closeGeWelcomePopup(): boolean {
  const manager = (window as GeWindow).GlobalE?.WelcomeManager;
  if (typeof manager?.CloseWelcome !== "function") {
    console.warn(
      "[GlobalE] CloseWelcome 없음 — 웰컴 팝업을 닫지 못하고 스위처만 연다",
    );
    return false;
  }
  try {
    manager.CloseWelcome();
    return true;
  } catch (e) {
    console.warn("[GlobalE] CloseWelcome 실패 — 스위처만 연다", e);
    return false;
  }
}

/**
 * ## ★GE 자기 스위처도 닫는다 — 웰컴을 닫으면 GE 가 곧바로 자기 스위처를 띄운다
 *
 * dev(INT, AU) 실측 2026-09-29: 링크를 누르면 GE 가 `OnBeforeSwitcher` 를 먼저 발생시키고, **웰컴 팝업이
 * 닫히는 순간** 대기해 둔 자기 국가·통화 창(`#globale_csc_popup`, 「NOW SHIPPING WORLDWIDE! … SAVE /
 * CANCEL」, z-index 1,000,001)을 띄운다 — 클릭 시점에 이미 떠 있거나, 닫은 뒤 7~26ms 에 뜬다(두 모양 모두 관찰, `GE_SWITCHER_MAX_CLOSES` 주석). 우리 다이얼로그(z-index 100)가 그 아래에
 * 깔린다. 웰컴을 안 닫으면 4초가 지나도 안 뜬다.
 *
 * **GE 창을 그대로 쓰지 않는 이유**(실측): GE 창에서 AU → JP 로 SAVE 하면 GE 가 `GlobalE_Data` 만 바꾸고
 * 새로고침하는데, 새 페이지에서 `GlobalEScripts` 가 엔진을 넣기 전에 그 쿠키를 우리 국가(`bh_ccode`=AU)로
 * 다시 쓴다 → 최종 AU. (실측 당시엔 지금은 없는 `useSyncGlobalEData` 가 되돌렸다 — 결과는 같다.)
 * 되돌리지 않더라도 사이트 국가·가격·언어는 AU 로 남아 GE 와 어긋난다. GE 창엔 KR 도 없다.
 * 국가를 바꾸는 창구는 **우리 스위처 하나**여야 한다.
 *
 * ⚠️ **너무 일찍 닫으면 오버레이가 남는다** — 스위처가 없을 때 `ShippingSwitcher.Close()` 를 부르면
 * `OnShippingSwitcherClosed` 는 나지만 회색 오버레이(`#globale_overlay`)가 남았다(실측). 그래서 DOM 에
 * 생긴 것이 아니라 **보이는 순간**에 닫는다. 보인 뒤 닫으면 창·오버레이 모두 사라졌다(실측).
 */
const GE_SWITCHER_ID = "globale_csc_popup";
/** GE 스위처가 뜨기를 기다리는 상한. 실측 24ms — 넉넉히 잡되, GE 가 설정을 끄면 여기서 조용히 끝난다. */
export const GE_SWITCHER_WATCH_MS = 3000;

function isShown(el: HTMLElement | null): el is HTMLElement {
  return !!el && (el.offsetWidth > 0 || el.offsetHeight > 0);
}

function closeGeSwitcher(): void {
  // 객체에 붙여서 부른다 — 이유는 `closeGeWelcomePopup` 주석.
  const switcher = (window as GeWindow).GlobalE?.ShippingSwitcher;
  if (typeof switcher?.Close !== "function") {
    console.warn(
      "[GlobalE] ShippingSwitcher.Close 없음 — GE 스위처가 우리 스위처를 덮을 수 있다",
    );
    return;
  }
  try {
    switcher.Close();
  } catch (e) {
    console.warn("[GlobalE] ShippingSwitcher.Close 실패", e);
  }
}

/**
 * 지금 걸려 있는 관찰의 정지 함수. **관찰은 한 번에 하나다** — 핸들러가 두 번 불리면(두 큐에서 이벤트가
 * 오는 경우 등) 관찰자가 둘 생겨 GE 창을 두 번 닫았다(테스트 실측). 새로 걸면 이전 것을 먼저 멈춘다.
 */
let stopActiveWatch: (() => void) | null = null;

/**
 * 관찰 창 안에서 GE 스위처를 닫는 **최대 횟수**.
 *
 * GE 가 스위처를 띄우는 모양이 dev 실측에서 **세 가지**였다(2026-09-29, 매 회 쿠키 초기화 · 누계):
 *
 * ```
 *   (A) 클릭 시점엔 없고, 웰컴을 닫은 뒤 7~26ms 에 비동기로 뜬다       4 회   → 관찰자가 닫는다
 *   (B) 클릭 시점에 이미 떠 있다(`event csc=true`)                      17 회   → 즉시 닫는다
 *   (C) 끝까지 안 뜬다                                                2 회   → 할 일 없음
 * ```
 *
 * 셋 모두 끝에는 우리 스위처만 남았다(+1초 · +3.5초 시점 확인). 리뷰 #5849 R1 이 짚은 합친 모양 —
 * **이미 떠 있는데 웰컴을 닫자 비동기로 다시 뜬다** — 은 관찰되지 않았지만, GE 가 이미 세 모양 사이에서
 * 흔들리므로 없다고 장담하지 않는다. 그래서 **한 번 더**까지 닫는다(즉시 닫은 뒤에도 창 안에서 지켜본다).
 *
 * 무제한이 아닌 이유: 순서를 잘못 두면(웰컴보다 GE 스위처를 먼저 닫으면) GE 가 계속 다시 띄워
 * `Close()` 가 0.5초에 수백 번 돌았다(실측). 상한이 그 루프를 끊는다.
 */
export const GE_SWITCHER_MAX_CLOSES = 2;

/**
 * GE 스위처가 보이면 닫는다. 이미 보이면 즉시 닫고, 그 뒤로도 `GE_SWITCHER_WATCH_MS` 동안 지켜보며
 * 합쳐 `GE_SWITCHER_MAX_CLOSES` 번까지 닫는다.
 */
export function closeGeSwitcherWhenShown(): void {
  stopActiveWatch?.();
  let closes = 0;
  const closeIfShown = (): boolean => {
    if (!isShown(document.getElementById(GE_SWITCHER_ID))) return false;
    closes++;
    closeGeSwitcher();
    return true;
  };
  closeIfShown();
  if (closes >= GE_SWITCHER_MAX_CLOSES) return;

  const observer = new MutationObserver(() => {
    if (closeIfShown() && closes >= GE_SWITCHER_MAX_CLOSES) stop();
  });
  const timer = window.setTimeout(() => stop(), GE_SWITCHER_WATCH_MS);
  function stop() {
    observer.disconnect();
    window.clearTimeout(timer);
    if (stopActiveWatch === stop) stopActiveWatch = null;
  }
  stopActiveWatch = stop;
  // 보이게 되는 건 노드 추가일 수도, style 변경일 수도 있다 — 둘 다 본다.
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["style", "class"],
  });
}

/**
 * ## ★★순서가 결과를 가른다 — 웰컴을 **먼저** 닫고, **그다음** GE 스위처를 닫는다
 *
 * GE 는 웰컴 팝업이 열린 채로 스위처가 닫히면 **웰컴을 닫는 순간 스위처를 다시 띄운다.** dev(INT, AU)
 * 실측 2026-09-29, 매 회 쿠키를 지우고 새로 연 페이지에서:
 *
 * ```
 *   GE 스위처 지켜보기 먼저 → 웰컴 닫기 · 한 번 닫고 끝     5/5  GE 스위처가 남는다(웰컴을 닫자 다시 뜸)
 *   GE 스위처 지켜보기 먼저 → 웰컴 닫기 · 계속 지켜봄        4/4  GE 스위처가 남는다 + Close() 가 0.5초에 수백 번 돈다
 *   웰컴 먼저 닫기 → GE 스위처 닫기                          5/5  깨끗 — 우리 스위처만 남는다
 * ```
 *
 * ⚠️ 한때 「순서는 상관없다」고 적고 순서 테스트를 지웠다. **jsdom 가짜로만 판단한 오판**이었다 — 가짜 GE 는
 * 「웰컴을 닫으면 스위처를 다시 띄운다」를 흉내 내지 않았다. 실제 GE 로 재자 순서가 결과를 갈랐다.
 * 그래서 순서 테스트는 이 GE 동작을 흉내 내게 되살렸다(`welcome-change-country-ge-switcher.test.ts`).
 *
 * 「계속 지켜보기」는 해법이 아니다 — 닫을 때마다 DOM 이 바뀌어 관찰자가 다시 깨고, 계속 `Close()` 를 부른다.
 * 그래서 GE 스위처는 **`GE_SWITCHER_MAX_CLOSES` 번 닫으면 관찰을 멈춘다** — 상한이 그 루프를 끊는다.
 *
 * 우리 스위처를 여는 자리(끝)는 결과에 영향이 없다 — GE 창 둘은 우리 다이얼로그가 그려지기 전에 닫힌다.
 */
export function onChangeShippingCountryClicked(): void {
  closeGeWelcomePopup();
  closeGeSwitcherWhenShown();
  openLocaleSwitcher();
}

let registered = false;

/**
 * `GlobalEScripts` 가 GE 활성(flag ON) 시 호출. flag OFF(prod 현재)면 호출되지 않아 inert.
 * 모듈 싱글턴 가드로 SPA 내 재호출은 무해하다.
 */
export function registerGeWelcomeChangeCountry(): void {
  if (typeof window === "undefined" || registered) return;
  registered = true;
  onGeSdkEvent(
    "OnChangeShippingCountryClicked",
    onChangeShippingCountryClicked,
  );
}
